import { AppBreadcrumb, AppCard, AppContainer } from '@/components/atoms';
import { EditTownForm } from '@/components/organisms';
import type { TownEditInitialData } from '@/components/organisms/EditTownForm';
import { LoadingTemplate, SiteAdminNotFoundTemplate } from '@/components/templates';
import { useTownQuery } from '@/hooks/admin/useTownQuery';
import { useUpdateTownMutation } from '@/hooks/admin/useUpdateTownMutation';
import useAppToast from '@/hooks/useAppToast';
import type { TownEditFormData } from '@/schemas/townSchema';
import { getApiErrorMessage, isNotFoundError } from '@/utils/apiError';
import { AxiosError } from 'axios';
import React, { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

type ApiFieldErrors = Partial<Record<keyof TownEditFormData, string>>;

interface ApiValidationErrorResponse {
  message?: string;
  errors?: Record<string, string[]>;
}

const SiteAdminTownEditPage: React.FC = () => {
  const { site, id } = useParams<{ site: string; id: string }>();
  const currentSite = site ?? 'medix';
  const recordId = Number(id) || 0;
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useAppToast();

  const locationState = location.state as { listSearch?: string } | null;
  const listSearch = locationState?.listSearch ?? '';
  const listPath = `/${currentSite}/admin/towns${listSearch}`;

  const { data, isLoading, isError } = useTownQuery(currentSite, recordId);
  const updateMutation = useUpdateTownMutation();
  const [apiErrors, setApiErrors] = useState<ApiFieldErrors>({});

  const record = data?.data;

  // Keep the successfully loaded town so a background refetch 404 (record soft-deleted in another tab)
  // doesn't unmount the form before the local 404 handler can navigate.
  const loadedRecordRef = useRef(record);
  if (record) loadedRecordRef.current = record;
  const stableRecord = record ?? loadedRecordRef.current;

  const initialData: TownEditInitialData | null = useMemo(() => {
    if (!stableRecord) return null;
    return {
      name: stableRecord.name ?? '',
      kana: stableRecord.kana ?? '',
      postal_code1: stableRecord.postal_code1 ?? '',
      postal_code2: stableRecord.postal_code2 ?? '',
      search_name: stableRecord.search_name ?? '',
    };
  }, [stableRecord]);

  if (isLoading) return <LoadingTemplate />;
  if ((isError && !stableRecord) || !initialData) return <SiteAdminNotFoundTemplate />;

  const breadcrumbItems = [{ title: '郵便番号', href: listPath }, { title: ':id' }];

  const handleSubmit = async (formData: TownEditFormData) => {
    setApiErrors({});
    try {
      await updateMutation.mutateAsync({
        siteType: currentSite,
        id: recordId,
        data: formData,
      });
      toast.success('更新しました。');
      navigate(listPath, { replace: true, state: { listSearch } });
    } catch (err) {
      if (isNotFoundError(err)) {
        toast.error(
          getApiErrorMessage(err, 'エラーが発生しました。しばらくしてから再度お試しください。')
        );
        return;
      }
      const axiosError = err as AxiosError<ApiValidationErrorResponse>;
      const responseErrors = axiosError.response?.data?.errors;
      if (responseErrors) {
        const fieldErrors: ApiFieldErrors = {};
        for (const [field, messages] of Object.entries(responseErrors)) {
          if (messages && messages.length > 0) {
            fieldErrors[field as keyof TownEditFormData] = messages[0];
          }
        }
        setApiErrors(fieldErrors);
      }
      toast.error(getApiErrorMessage(err, 'エラーが発生しました。'));
    }
  };

  return (
    <AppContainer className="py-6 space-y-6">
      <AppBreadcrumb items={breadcrumbItems} paramsLabel="編集" />
      <AppCard title={`郵便番号編集:${recordId}`}>
        <EditTownForm
          key={`town-edit-${recordId}`}
          initialData={initialData}
          onSubmit={handleSubmit}
          isLoading={updateMutation.isPending}
          apiErrors={apiErrors}
        />
      </AppCard>
    </AppContainer>
  );
};

export default SiteAdminTownEditPage;
